/**
 * Deepgram STT Service (Client-Side)
 * 
 * Provides real-time speech-to-text using Deepgram Nova-3 streaming.
 * Uses WebSocket for direct connection to Deepgram servers.
 * 
 * For Swahili and other non-streaming languages, falls back to batch
 * transcription via Firebase Cloud Function.
 * 
 * IMPORTANT: This service is for ChartMind Translation (Nova-3).
 * For voice agent with turn detection, use deepgramFluxService.
 * 
 * Endpoint: /v1/listen (NOT /v2/listen - that's for Flux)
 */

import { getFunctions, httpsCallable } from 'firebase/functions';

// STT Engine types
export const STT_ENGINE = {
  WEB_SPEECH: 'web_speech',
  DEEPGRAM_NOVA3: 'deepgram_nova3',
  DEEPGRAM_WHISPER: 'deepgram_whisper',
  OPENAI_WHISPER: 'openai_whisper',
};

// WebSocket ready states
const WS_READY_STATE = {
  CONNECTING: 0,
  OPEN: 1,
  CLOSING: 2,
  CLOSED: 3,
};

// Supported languages for Nova-3 code-switching
const CODE_SWITCHING_LANGUAGES = ['en', 'es', 'fr', 'de', 'hi', 'ru', 'pt', 'ja', 'it', 'nl'];

// Special multilingual mode - Nova-3 handles automatic language detection
const MULTILINGUAL_MODE = 'multi';

// Languages that require Whisper Cloud (batch mode)
const WHISPER_ONLY_LANGUAGES = ['sw']; // Swahili

/**
 * Deepgram STT Service Class
 * 
 * Manages WebSocket connection to Deepgram Nova-3 for real-time transcription.
 */
class DeepgramSTTService {
  constructor() {
    this.websocket = null;
    this.isConnected = false;
    this.isConnecting = false;
    this.keepAliveInterval = null;
    this.reconnectAttempts = 0;
    this.maxReconnectAttempts = 3;
    
    // Callbacks
    this.onTranscript = null;
    this.onInterimTranscript = null;
    this.onError = null;
    this.onConnectionChange = null;
    
    // Configuration
    // Deepgram recommends endpointing=100 for code-switching/multilingual
    this.config = {
      language: 'en-US',
      enableCodeSwitching: true,
      encoding: 'linear16',
      sampleRate: 16000,
      channels: 1,
      endpointing: 100,       // 100ms - Deepgram recommended for code-switching
      utterance_end_ms: 1000, // Finalize utterances after 1s of silence
      punctuate: true,
      diarize: false,        // When true, response words include speaker id (0, 1, ...)
    };
    
    // Firebase functions reference
    this.functions = getFunctions();
    this.deepgramTokenFn = httpsCallable(this.functions, 'deepgramToken');
    this.deepgramSTTFn = httpsCallable(this.functions, 'deepgramSTT');
  }

  /**
   * Check if using multilingual mode
   * @param {string} language - Language code
   * @returns {boolean}
   */
  isMultilingualMode(language) {
    return language === MULTILINGUAL_MODE || language === 'multi';
  }

  /**
   * Check if a language requires batch processing (no streaming)
   * @param {string} language - Language code
   * @returns {boolean}
   */
  requiresBatchProcessing(language) {
    // Multilingual mode doesn't require batch processing
    if (this.isMultilingualMode(language)) return false;
    const langBase = language.split('-')[0].toLowerCase();
    return WHISPER_ONLY_LANGUAGES.includes(langBase);
  }

  /**
   * Check if a language supports code-switching
   * @param {string} language - Language code
   * @returns {boolean}
   */
  supportsCodeSwitching(language) {
    // Multilingual mode always supports code-switching
    if (this.isMultilingualMode(language)) return true;
    const langBase = language.split('-')[0].toLowerCase();
    return CODE_SWITCHING_LANGUAGES.includes(langBase);
  }

  /**
   * Initialize the service with configuration
   * @param {Object} config - Configuration options
   */
  configure(config = {}) {
    this.config = {
      ...this.config,
      ...config,
    };
  }

  /**
   * Set callback handlers
   * @param {Object} handlers - { onTranscript, onInterimTranscript, onError, onConnectionChange }
   */
  setHandlers(handlers) {
    if (handlers.onTranscript) this.onTranscript = handlers.onTranscript;
    if (handlers.onInterimTranscript) this.onInterimTranscript = handlers.onInterimTranscript;
    if (handlers.onError) this.onError = handlers.onError;
    if (handlers.onConnectionChange) this.onConnectionChange = handlers.onConnectionChange;
  }

  /**
   * Connect to Deepgram streaming API
   * @returns {Promise<boolean>} Success status
   */
  async connect() {
    if (this.isConnected || this.isConnecting) {
      return true;
    }

    // Check if language requires batch processing
    if (this.requiresBatchProcessing(this.config.language)) {
      return false;
    }

    this.isConnecting = true;
    this.onConnectionChange?.('connecting');

    try {
      // Get WebSocket token from Firebase function
      
      const tokenResult = await this.deepgramTokenFn({
        mode: 'nova3',
        language: this.config.language,
        options: {
          model: 'nova-3',
          enableCodeSwitching: this.config.enableCodeSwitching && 
                               this.supportsCodeSwitching(this.config.language),
          encoding: this.config.encoding,
          sample_rate: this.config.sampleRate,
          channels: this.config.channels,
          interim_results: true,
          punctuate: this.config.punctuate,
          endpointing: this.config.endpointing,       // 100ms for code-switching
          utterance_end_ms: this.config.utterance_end_ms, // 1000ms silence threshold
          diarize: this.config.diarize === true,
        },
      });

      const { apiKey, wsUrl } = tokenResult.data;

      if (!apiKey || !wsUrl) {
        throw new Error('Failed to get Deepgram credentials');
      }


      // Create WebSocket connection
      this.websocket = new WebSocket(wsUrl, ['token', apiKey]);

      // Set up WebSocket event handlers
      this.websocket.onopen = this._handleOpen.bind(this);
      this.websocket.onmessage = this._handleMessage.bind(this);
      this.websocket.onerror = this._handleError.bind(this);
      this.websocket.onclose = this._handleClose.bind(this);

      // Wait for connection to open
      await new Promise((resolve, reject) => {
        const timeout = setTimeout(() => {
          reject(new Error('WebSocket connection timeout'));
        }, 10000);

        const originalOnOpen = this.websocket.onopen;
        this.websocket.onopen = (event) => {
          clearTimeout(timeout);
          originalOnOpen?.call(this, event);
          resolve();
        };

        const originalOnError = this.websocket.onerror;
        this.websocket.onerror = (event) => {
          clearTimeout(timeout);
          originalOnError?.call(this, event);
          reject(new Error('WebSocket connection failed'));
        };
      });

      return true;

    } catch (error) {
      console.error('[DeepgramSTT] Connection error:', error);
      this.isConnecting = false;
      this.onError?.(error);
      this.onConnectionChange?.('error');
      return false;
    }
  }

  /**
   * Handle WebSocket open event
   */
  _handleOpen() {
    this.isConnected = true;
    this.isConnecting = false;
    this.reconnectAttempts = 0;
    this.onConnectionChange?.('connected');

    // Start keep-alive interval (Deepgram closes after 12s of inactivity)
    this._startKeepAlive();
  }

  /**
   * Handle incoming WebSocket messages
   * @param {MessageEvent} event - WebSocket message event
   */
  _handleMessage(event) {
    try {
      const data = JSON.parse(event.data);

      // Handle different message types
      if (data.type === 'Results') {
        const channel = data.channel;
        const alternative = channel?.alternatives?.[0];

        if (alternative && alternative.transcript) {
          const transcript = alternative.transcript;
          const isFinal = data.is_final;
          const speechFinal = data.speech_final;
          const words = alternative.words || [];
          // When diarize=true, words have speaker (0, 1, ...); use first word's speaker or majority
          let speakerId = undefined;
          if (words.length > 0 && typeof words[0].speaker === 'number') {
            const counts = {};
            words.forEach((w) => { counts[w.speaker] = (counts[w.speaker] || 0) + 1; });
            speakerId = parseInt(
              Object.entries(counts).sort((a, b) => b[1] - a[1])[0][0],
              10
            );
          }

          if (isFinal || speechFinal) {
            // Final transcript
            this.onTranscript?.({
              text: transcript,
              confidence: alternative.confidence,
              words: alternative.words,
              isFinal: true,
              speechFinal,
              speakerId,
            });
          } else {
            // Interim transcript
            this.onInterimTranscript?.({
              text: transcript,
              confidence: alternative.confidence,
              isFinal: false,
              speakerId,
            });
          }
        }
      } else if (data.type === 'Metadata') {
      } else if (data.type === 'Error') {
        console.error('[DeepgramSTT] Server error:', data);
        this.onError?.(new Error(data.message || 'Deepgram server error'));
      }

    } catch (error) {
      console.error('[DeepgramSTT] Error parsing message:', error);
    }
  }

  /**
   * Handle WebSocket error
   * @param {Event} event - Error event
   */
  _handleError(event) {
    console.error('[DeepgramSTT] WebSocket error:', event);
    this.onError?.(new Error('WebSocket error'));
  }

  /**
   * Handle WebSocket close
   * @param {CloseEvent} event - Close event
   */
  _handleClose(event) {
    this.isConnected = false;
    this.isConnecting = false;
    this._stopKeepAlive();
    this.onConnectionChange?.('disconnected');

    // Attempt reconnection if unexpected close
    if (event.code !== 1000 && this.reconnectAttempts < this.maxReconnectAttempts) {
      this.reconnectAttempts++;
      setTimeout(() => this.connect(), 1000 * this.reconnectAttempts);
    }
  }

  /**
   * Start keep-alive interval
   */
  _startKeepAlive() {
    this._stopKeepAlive();
    // Send keep-alive every 10 seconds (Deepgram timeout is 12s)
    this.keepAliveInterval = setInterval(() => {
      this.sendKeepAlive();
    }, 10000);
  }

  /**
   * Stop keep-alive interval
   */
  _stopKeepAlive() {
    if (this.keepAliveInterval) {
      clearInterval(this.keepAliveInterval);
      this.keepAliveInterval = null;
    }
  }

  /**
   * Send keep-alive message
   */
  sendKeepAlive() {
    if (this.isConnected && this.websocket?.readyState === WS_READY_STATE.OPEN) {
      // Deepgram uses a JSON keepalive message
      this.websocket.send(JSON.stringify({ type: 'KeepAlive' }));
    }
  }

  /**
   * Send audio data to Deepgram
   * @param {ArrayBuffer|Uint8Array} audioData - Raw audio data (linear16 PCM)
   */
  sendAudio(audioData) {
    if (!this.isConnected || this.websocket?.readyState !== WS_READY_STATE.OPEN) {
      console.warn('[DeepgramSTT] Cannot send audio - not connected');
      return false;
    }

    try {
      this.websocket.send(audioData);
      return true;
    } catch (error) {
      console.error('[DeepgramSTT] Error sending audio:', error);
      this.onError?.(error);
      return false;
    }
  }

  /**
   * Disconnect from Deepgram
   */
  disconnect() {
    this._stopKeepAlive();

    if (this.websocket) {
      // Send close message to finalize any pending transcription
      if (this.websocket.readyState === WS_READY_STATE.OPEN) {
        try {
          this.websocket.send(JSON.stringify({ type: 'CloseStream' }));
        } catch (e) {
          // Ignore errors when closing
        }
      }
      this.websocket.close(1000, 'Client disconnect');
      this.websocket = null;
    }

    this.isConnected = false;
    this.isConnecting = false;
    this.onConnectionChange?.('disconnected');
  }

  /**
   * Transcribe audio using batch mode (Whisper Cloud)
   * Used for languages that don't support streaming (e.g., Swahili)
   * 
   * @param {string} audioBase64 - Base64-encoded audio
   * @param {string} language - Language code
   * @returns {Promise<Object>} Transcription result
   */
  async transcribeBatch(audioBase64, language = 'en-US') {

    try {
      const result = await this.deepgramSTTFn({
        audio: audioBase64,
        language,
        options: {},
      });

      return result.data;

    } catch (error) {
      console.error('[DeepgramSTT] Batch transcription error:', error);
      throw error;
    }
  }

  /**
   * Get connection status
   * @returns {Object} Status information
   */
  getStatus() {
    return {
      isConnected: this.isConnected,
      isConnecting: this.isConnecting,
      readyState: this.websocket?.readyState,
      config: this.config,
    };
  }
}

// Export singleton instance
export const deepgramSTTService = new DeepgramSTTService();

// Export class for testing
export { DeepgramSTTService };

export default deepgramSTTService;
